import { AIChatInput } from "@/components/AIChatInput/AIChatInput";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { ChatFileManager } from "@/components/file/ChatFileManager/ChatFileManager";
import { ChatFileManagerAttachmentButton } from "@/components/file/ChatFileManager/ChatFileManagerAttachmentButton";
import {
  ChatFileManagerProvider,
  useChatFileManager,
} from "@/components/file/ChatFileManager/ChatFileManagerContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useKitsData } from "@/data/use-kits-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import { GetKitDto } from "@/lib/gen-api";
import { getInitials } from "@/lib/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useCreateTemplateCreatorAction } from "../../action/use-create-template-creator-action";
import { Checkbox } from "../../components/ui/checkbox";

export default function TemplatesPage() {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { kits } = useKitsData(groupUuid, { type: "template" });
  const groupedTemplates = groupByDate(kits, "date_updated");

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Created By" },
  ];

  if (!groupUuid) return null;

  return (
    <>
      <Page>
        <PageHeader>
          <PageHeader.Title>Group Document Templates</PageHeader.Title>
        </PageHeader>

        <PageBody>
          <Column>
            <p>
              This is where you set up standard templates for your team. Think
              <span className="text-accent font-semibold">
                {" "}
                campaign briefs
              </span>
              , <span className="text-accent font-semibold">media plans</span>,
              or{" "}
              <span className="text-accent font-semibold">project plans</span>.
              Using consistent templates makes it easier for everyone to work
              together. It also teaches your{" "}
              <span className="text-accent font-semibold">Expert AIs</span> how
              to draft, fill in, and evaluate these key documents to your team's
              exact standards.
            </p>
          </Column>
          <Row>
            <Button onClick={() => setIsDialogOpen(true)}>
              Create Template
            </Button>
          </Row>
          <GroupedTable
            data={groupedTemplates}
            columns={columns}
            itemIdKey="uuid"
            renderCell={{
              name: (_, row) => <KitDisplayName kit={row} />,
              user_uuid: (value) => <UserCell userUuid={value} />,
              date_updated: (value) => smartDate(value),
            }}
            renderActions={({ row }) => (
              <KitDeletionDialog groupUuid={row.group_uuid} kitUuid={row.uuid}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:bg-destructive hover:text-destructive-foreground"
                  aria-label="Delete"
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </KitDeletionDialog>
            )}
            rowLink={(row) => ({
              to: "/w/$groupSlug/k/$kitUuid/template",
              params: { groupSlug, kitUuid: row.uuid },
            })}
            emptyMessage="No templates here yet. Create your first one to standardize your team's documents and teach your Expert AIs."
          />
        </PageBody>
      </Page>

      <ChatFileManagerProvider
        groupUuid={groupUuid}
        kitUuid={null}
        chatUuid={null}
      >
        <CreateTemplateDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          groupUuid={groupUuid}
        />
      </ChatFileManagerProvider>
    </>
  );
}

const CreateTemplateDialog = ({
  open,
  onOpenChange,
  groupUuid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string | null;
}) => {
  const groupSlug = useGroupSlug();
  const [templateName, setTemplateName] = useState("Untitled Template");
  const [generateContent, setGenerateContent] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [templateType, setTemplateType] = useState<"rpf" | "default">(
    "default",
  );
  const { createEmpty, createWithContent, isCreating } =
    useCreateTemplateCreatorAction(groupUuid);
  const { selectedLocalFiles, selectedExternalFiles, clearFileManager } =
    useChatFileManager();
  const navigate = useNavigate();

  async function handleCreateTemplate() {
    if (!templateName.trim()) return;
    const name = templateName.trim();

    let kitUuid: string | undefined;
    if (generateContent) {
      kitUuid = await createWithContent({
        name,
        localFiles: selectedLocalFiles,
        externalFiles: selectedExternalFiles,
        userContext: prompt,
        type: templateType,
      });
    } else {
      kitUuid = await createEmpty({ name });
    }

    setTemplateName("Untitled Template");
    clearFileManager();
    onOpenChange(false);
    toast.success("Template created successfully!");
    if (kitUuid) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/template",
        params: { groupSlug: groupSlug!, kitUuid: kitUuid },
      });
    }
  }

  const filePickerElement = (
    <ChatFileManagerAttachmentButton library={false} disabled={isCreating} />
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription>
              Enter a name for your new template.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Name"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateTemplate();
              }}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="generate-content"
              checked={generateContent}
              onCheckedChange={(checked) => setGenerateContent(!!checked)}
            />
            <label
              htmlFor="generate-content"
              className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Generate content
            </label>
          </div>
          {generateContent && (
            <>
              <Select
                value={templateType}
                onValueChange={(value) =>
                  setTemplateType(value as "rpf" | "default")
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="rpf">RFP</SelectItem>
                </SelectContent>
              </Select>

              <AIChatInput
                placeholder="Provide additional context or instructions"
                value={prompt}
                onChange={setPrompt}
                filePickerElement={filePickerElement}
                enableCommand={false}
                expectExpertSelection={false}
                disabled={isCreating}
                isPending={isCreating}
              />
              <ChatFileManager goal="select" />
            </>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleCreateTemplate}
              size="sm"
              disabled={isCreating}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LoadingOverlay
        loading={isCreating}
        title="Creating template"
        description="This may take a minute or two. Please don't close this tab."
      />
    </>
  );
};

function UserCell({ userUuid }: { userUuid: string }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  return (
    <Row className="items-center gap-2">
      <Avatar className="h-5 w-5">
        <AvatarFallback>
          {getInitials(getUserForUserUuid(userUuid)?.name || "", 2)}
        </AvatarFallback>
        <AvatarImage
          src={getUserForUserUuid(userUuid)?.photo_url ?? undefined}
        />
      </Avatar>
      <span>{getUserForUserUuid(userUuid)?.name}</span>
    </Row>
  );
}
